import React, { useRef, ChangeEvent } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';

const formatNumber = (inputValue: string) => {
  const cleanedValue = inputValue.replace(/[^0-9.]/g, ''); // Remove non-numeric characters except decimal point

  const parts = cleanedValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Limit the decimal places to a maximum of two
  if (parts[1]) {
    parts[1] = parts[1].slice(0, 2);
  }

  return parts.join('.');
};

const getCaretPosition = (newValue: string, prevValue: string, currentPos: number) => {
  // Count commas before cursor in both old and new value
  const oldCommas = (prevValue.slice(0, currentPos).match(/,/g) || []).length;
  const newCommas = (newValue.slice(0, currentPos).match(/,/g) || []).length;
  
  // Adjust position based on difference in commas
  return currentPos + (newCommas - oldCommas);
};

const handleInputChange = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setAmount: (value: string) => void,
  inputRef: React.RefObject<HTMLInputElement>
) => {
  const input = e.target;
  const cursorPos = input.selectionStart || 0;
  const prevValue = input.value;
  let value = input.value;

  const splitValue = value.replaceAll(',', '').split('.');
  if (splitValue[0].length < 13) {
    // Restrict the number from starting with a 0
    if ((value.length === 1 && value === '0') || (value.length === 1 && value === '.')) {
      setAmount('');
      return;
    }

    const cleanValue = value.replace(/[^0-9.]/g, ''); // Remove non-numeric characters except decimal point
    const formattedValue = formatNumber(cleanValue);
    setAmount(formattedValue);

    // Calculate new cursor position
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newPos = getCaretPosition(formattedValue, prevValue, cursorPos);
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    });
  }
};

const isLastCharADecimal = (amount: string) => amount[amount.length - 1] === '.';
const hasOneDecimal = (amount: string) => amount.split('.')[1]?.length === 1;
const hasNoDecimal = (amount: string) => !amount.includes('.');

const handleBlur = (value: string, setAmount: (value: string) => void) => {
  if (value.length > 0) {
    let newValue = value;
    // Add ".00" if it has no decimal
    if (hasNoDecimal(value)) {
      newValue += '.00';
      // Add "00" if last char is decimal
    } else if (isLastCharADecimal(value)) {
      newValue += '00';
      // Add '0' if only one decimal
    } else if (hasOneDecimal(value)) {
      newValue += '0';
    }
    setAmount(newValue);
  }
};

type AmountAndCurrencyProps = {
  setCurrency?: (value: any) => void;
  amount: string;
  setAmount: (value: string) => void;
  touched?: FormikTouched<any>;
  errors?: FormikErrors<any>;
  values?: AmountAndCurrencyProps;
  placeholder?: string;
  label?: string;
};

export const AmountAndCurrency = ({
  setCurrency,
  amount,
  setAmount,
  touched,
  errors,
  placeholder,
  label
}: AmountAndCurrencyProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Grid item md={4} lg={4}>
        <TextField
          inputRef={inputRef}
          type="text"
          autoComplete="off"
          id="amount"
          label={label || 'Value (Mandatory)'}
          sx={{ minWidth: '97%' }}
          placeholder={placeholder || 'Enter Value (Mandatory)'}
          value={amount}
          onBlur={(e) => handleBlur(e.target.value, setAmount)}
          onChange={(e) => handleInputChange(e, setAmount, inputRef)}
          InputLabelProps={{ shrink: true, inputMode: 'numeric' }}
          onWheel={(event) => {
            (event.target as HTMLInputElement).blur();
          }}
          error={Boolean(touched?.amount && errors?.amount)}
          helperText={touched?.amount && errors?.amount}
        />
      </Grid>
      {setCurrency && (
        <>
          <Grid item md={1} lg={1} />
          <Grid item md={2} lg={2}>
            <FormControl>
              <InputLabel id="input-label">Currency</InputLabel>
              <Select
                labelId="currency-label"
                id="currency-label"
                label="currency-name"
                defaultValue="AUD"
                onChange={setCurrency}
              >
                <MenuItem value="AUD">AUD</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}
    </>
  );
};

export default AmountAndCurrency;